import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageSquare, Mail, CheckCircle, ChevronRight, ChevronDown, Send, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { StatCard } from '../components/dashboard/StatCard';
import { ChannelIcon } from '../components/shared/ChannelIcon';
import { StatCardSkeleton, TableSkeleton, LoadingBar } from '../components/shared/Skeletons';
import { EmptyState } from '../components/shared/EmptyState';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getMessageThemes(message) {
  if (Array.isArray(message.themes) && message.themes.length > 0) return message.themes;
  if (message.theme) return [message.theme];
  return [];
}

function formatDate(date) {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function formatAgo(date) {
  const ms = Date.now() - new Date(date).getTime();
  const mins = Math.floor(ms / 60000);
  const hrs  = Math.floor(ms / 3600000);
  const days = Math.floor(ms / 86400000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs  < 24) return `${hrs}h ago`;
  return `${days}d ago`;
}

// ─── Sentiment config ──────────────────────────────────────────────────────────

const sentimentConfig = {
  very_positive: { color: 'text-[#2e4d6e]',  bg: 'bg-[#395b81]/10 border border-[#395b81]/30', label: 'Very Positive' },
  positive:      { color: 'text-[#547a84]',  bg: 'bg-[#547a84]/10 border border-[#547a84]/30', label: 'Positive' },
  neutral:       { color: 'text-[#7a7a8a]',  bg: 'bg-[#38394e]/8  border border-[#38394e]/15', label: 'Neutral' },
  negative:      { color: 'text-[#c06a55]',  bg: 'bg-[#f0856d]/10 border border-[#f0856d]/30', label: 'Negative' },
  very_negative: { color: 'text-[#a0442e]',  bg: 'bg-[#f0856d]/20 border border-[#f0856d]/40', label: 'Very Negative' },
};

// ─── Status pill ───────────────────────────────────────────────────────────────

function StatusPill({ status }) {
  const cfg = {
    new:      'bg-[#f0856d]/10 border border-[#f0856d]/30 text-[#c06a55]',
    replied:  'bg-[#547a84]/10 border border-[#547a84]/30 text-[#547a84]',
    reviewed: 'bg-[#395b81]/10 border border-[#395b81]/30 text-[#395b81]',
  }[status] || 'bg-[#38394e]/8 border border-[#38394e]/15 text-[#7a7a8a]';

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${cfg}`}>
      {status}
    </span>
  );
}

// ─── Reply status pill ─────────────────────────────────────────────────────────

function ReplyStatusPill({ status, failedCount }) {
  if (status === 'sent' && failedCount > 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-[#f0856d]/10 border border-[#f0856d]/30 text-[#c06a55]">
        <AlertCircle className="h-3 w-3" />
        Partial
      </span>
    );
  }
  const cfg = {
    sent:    'bg-[#547a84]/10 border border-[#547a84]/30 text-[#547a84]',
    failed:  'bg-[#f0856d]/20 border border-[#f0856d]/40 text-[#a0442e]',
    sending: 'bg-[#395b81]/10 border border-[#395b81]/30 text-[#395b81]',
    draft:   'bg-[#38394e]/8  border border-[#38394e]/15 text-[#7a7a8a]',
  }[status] || 'bg-[#38394e]/8 border border-[#38394e]/15 text-[#7a7a8a]';

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${cfg}`}>
      {status}
    </span>
  );
}

// ─── Reply row (expandable) ────────────────────────────────────────────────────

function ReplyRow({ reply, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const navigate = useNavigate();
  const sentAt = reply.sentAt || reply.createdAt;

  // messageIds is now populated — could be objects or bare IDs depending on fetch
  const messages = (reply.messageIds || []).filter(m => m && typeof m === 'object');

  return (
    <>
      <tr
        onClick={() => setExpanded(p => !p)}
        className="cursor-pointer border-b border-border transition-colors hover:bg-muted/50 group"
      >
        {/* Admin */}
        <td className="p-3">
          <p className="text-sm font-medium text-foreground">{reply.adminName}</p>
        </td>

        {/* Topic */}
        <td className="p-3">
          {reply.theme ? (
            <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground capitalize">
              {reply.theme.replace(/-/g, ' ')}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </td>

        {/* Recipients */}
        <td className="p-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground font-medium">{reply.sentCount}</span>
            {reply.failedCount > 0 && (
              <span className="text-xs text-red-600 font-medium">
                · {reply.failedCount} failed
              </span>
            )}
          </div>
        </td>

        {/* Status */}
        <td className="p-3">
          <ReplyStatusPill status={reply.status} failedCount={reply.failedCount} />
        </td>

        {/* Sent at */}
        <td className="p-3">
          <p className="text-sm text-muted-foreground">{formatAgo(sentAt)}</p>
          <p className="text-xs text-muted-foreground/60">{formatDate(sentAt)}</p>
        </td>

        {/* Expand toggle */}
        <td className="p-3">
          <ChevronDown className={`h-4 w-4 text-muted-foreground/50 transition-transform group-hover:text-muted-foreground ${expanded ? 'rotate-180' : ''}`} />
        </td>
      </tr>

      {/* Expanded panel */}
      {expanded && (
        <tr className="border-b border-border bg-muted/20">
          <td colSpan={6} className="px-6 py-5">

            {/* Reply text */}
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Reply text
            </p>
            <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap rounded-lg bg-card border border-border px-4 py-3 mb-5">
              {reply.replyText}
            </p>

            {/* Messages this reply was sent to */}
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              Sent to {messages.length || reply.sentCount} message{(messages.length || reply.sentCount) !== 1 ? 's' : ''}
              {reply.failedCount > 0 && (
                <span className="text-red-600 ml-2 normal-case font-medium">
                  · {reply.failedCount} delivery failure{reply.failedCount !== 1 ? 's' : ''}
                </span>
              )}
            </p>

            {messages.length > 0 ? (
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/40 border-b border-border">
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sender</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Channel</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Received</th>
                      <th className="px-4 py-2 w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((msg, i) => (
                      <tr
                        key={msg._id}
                        onClick={(e) => { e.stopPropagation(); navigate(`/respond/${msg._id}`); }}
                        className={`cursor-pointer transition-colors hover:bg-accent/50 group/row ${i < messages.length - 1 ? 'border-b border-border' : ''}`}
                      >
                        <td className="px-4 py-2.5">
                          <p className="text-sm text-foreground font-medium">{msg.senderContact}</p>
                          {msg.senderName && (
                            <p className="text-xs text-muted-foreground">{msg.senderName}</p>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <ChannelIcon channel={msg.channel} />
                        </td>
                        <td className="px-4 py-2.5">
                          <StatusPill status={msg.status} />
                        </td>
                        <td className="px-4 py-2.5">
                          <p className="text-sm text-muted-foreground">{formatAgo(msg.receivedAt)}</p>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="text-xs font-medium text-primary opacity-0 group-hover/row:opacity-100 transition-opacity whitespace-nowrap">
                            View →
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Message details not available</p>
            )}

          </td>
        </tr>
      )}
    </>
  );
}

// ─── Replies tab ───────────────────────────────────────────────────────────────

function RepliesTab({ expandReplyId }) {
  const [replies, setReplies]   = useState([]);
  const [isLoading, setLoading]       = useState(true);
  const [error, setError]             = useState(null);

  useEffect(() => {
    const fetchReplies = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/admin/replies`);
        if (res.data.success) {
          setReplies(res.data.replies);
        }
      } catch (err) {
        console.error('Error fetching replies:', err);
        setError('Failed to load replies');
      } finally {
        setLoading(false);
      }
    };
    fetchReplies();
  }, []);

  const totalSent   = replies.reduce((s, r) => s + (r.sentCount   || 0), 0);
  const totalFailed = replies.reduce((s, r) => s + (r.failedCount || 0), 0);

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm">

      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-4 flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-foreground">
            {replies.length} repl{replies.length !== 1 ? 'ies' : 'y'} sent
          </h3>
          {!isLoading && replies.length > 0 && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {totalSent} delivered
              {totalFailed > 0 && (
                <span className="text-red-600 ml-2">· {totalFailed} failed</span>
              )}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
<div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Send className="h-3.5 w-3.5" />
            Click a row to see reply text
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="p-6"><TableSkeleton rows={4} /></div>
      ) : error ? (
        <div className="p-6">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      ) : replies.length === 0 ? (
        <div className="p-12 text-center">
          <Send className="h-8 w-8 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No replies sent yet</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Replies sent from the Respond page will appear here</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sent by</th>
                <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Topic</th>
                <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recipients</th>
                <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</th>
                <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sent</th>
                <th className="p-3 w-8" />
              </tr>
            </thead>
            <tbody>
              {replies.map(reply => (
                <ReplyRow key={reply._id} reply={reply} defaultExpanded={expandReplyId && reply._id === expandReplyId} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function Messages() {
  const [stats, setStats]               = useState(null);
  const [messages, setMessages]         = useState([]);
  const [isLoading, setIsLoading]       = useState(true);
  const navigate                        = useNavigate();
  const location                        = useLocation();

  // Initialise from router state immediately so first fetch uses correct filter
  const initialState = location.state || {};
  const [statusFilter, setStatusFilter] = useState('all');
  const [themeFilter, setThemeFilter]   = useState('all');
  const [activeTab, setActiveTab]       = useState(initialState.tab === 'replies' ? 'replies' : 'inbox');
  const [expandReplyId, setExpandReplyId] = useState(initialState.expandReplyId || null);
  const [errorFilter, setErrorFilter]   = useState(initialState.filter === 'errors');

  // Clear router state after reading so refresh doesn't re-apply
  useEffect(() => {
    if (location.state) window.history.replaceState({}, '');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async (hasErrors = false) => {
    setIsLoading(true);
    try {
      const msgsUrl = hasErrors
        ? `${API_URL}/api/admin/messages?hasErrors=true`
        : `${API_URL}/api/admin/messages`;
      const [statsRes, msgsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(msgsUrl),
      ]);
      if (statsRes.data.success) setStats(statsRes.data.stats);
      if (msgsRes.data.success) {
        const sorted = [...msgsRes.data.messages].sort(
          (a, b) => new Date(b.receivedAt) - new Date(a.receivedAt)
        );
        setMessages(sorted);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(errorFilter);
  }, [errorFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const THEME_OPTIONS = [...new Set(
    messages.flatMap(m => getMessageThemes(m))
  )].sort();

  const filtered = messages
    .filter(m => statusFilter === 'all' || m.status === statusFilter)
    .filter(m => {
      if (themeFilter === 'all') return true;
      const themes = getMessageThemes(m);
      return themes.includes(themeFilter);
    });

  const statCards = stats ? [
    { title: 'Total Messages', value: stats.total   || 0, icon: <MessageSquare className="h-6 w-6" /> },
    { title: 'New',            value: stats.new      || 0, icon: <Mail          className="h-6 w-6" /> },
    { title: 'Replied',        value: stats.replied  || 0, icon: <CheckCircle   className="h-6 w-6" /> },
  ] : [];

  return (
    <DashboardLayout>
      <LoadingBar isLoading={isLoading} />
      <div className="space-y-6 animate-fade-in">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Messages</h1>
          <p className="text-muted-foreground">All incoming stakeholder communications</p>
        </div>

        {/* Stat cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)
            : statCards.map((s, i) => (
                <StatCard key={i} title={s.title} value={s.value} icon={s.icon} />
              ))
          }
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {[
            { key: 'inbox',   label: 'Inbox',   count: messages.length },
            { key: 'replies', label: 'Replies', count: null },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px
                ${activeTab === tab.key
                  ? 'border-[#f0856d] text-[#f0856d]'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
            >
              {tab.label}
              {tab.count !== null && (
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold
                  ${activeTab === tab.key ? 'bg-[#f0856d]/15 text-[#f0856d]' : 'bg-muted text-muted-foreground'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'replies' ? (
          <RepliesTab expandReplyId={expandReplyId} />
        ) : (

          /* ── Inbox ── */
          <div className="rounded-lg border border-border bg-card shadow-sm">

            {/* Error filter banner */}
            {errorFilter && (
              <div className="flex items-center justify-between bg-red-50 border-b border-red-200 px-6 py-3">
                <p className="text-sm font-medium text-red-700">
                  Showing messages with processing errors only
                </p>
                <button
                  onClick={() => setErrorFilter(false)}
                  className="text-xs font-medium text-red-600 hover:text-red-800 underline"
                >
                  Clear filter
                </button>
              </div>
            )}

            {/* List header + filter */}
            <div className="flex items-center justify-between border-b border-border px-6 py-4 flex-wrap gap-3">
              <h3 className="font-semibold text-foreground">
                {filtered.length} message{filtered.length !== 1 ? 's' : ''}
                {statusFilter !== 'all' && (
                  <span className="ml-2 text-sm font-normal text-muted-foreground capitalize">· {statusFilter}</span>
                )}
                {themeFilter !== 'all' && (
                  <span className="ml-2 text-sm font-normal text-muted-foreground capitalize">· {themeFilter}</span>
                )}
              </h3>

              <div className="flex items-center gap-2 flex-wrap">
                {/* Theme filter */}
                <select
                  value={themeFilter}
                  onChange={e => setThemeFilter(e.target.value)}
                  className="text-sm border border-border rounded-lg px-3 py-1.5 bg-card text-foreground cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#f0856d]"
                >
                  <option value="all">All themes</option>
                  {THEME_OPTIONS.map(t => (
                    <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>

                {/* Status filter */}
                <div className="flex border border-border rounded-lg overflow-hidden text-sm">
                  {['all', 'new', 'replied'].map((f, i) => (
                    <button
                      key={f}
                      onClick={() => setStatusFilter(f)}
                      className={`px-4 py-1.5 font-medium capitalize transition-colors
                        ${statusFilter === f
                          ? 'bg-[#f0856d] text-white'
                          : 'bg-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }
                        ${i !== 0 ? 'border-l border-border' : ''}
                      `}
                    >
                      {f}
                      {f !== 'all' && stats?.[f] > 0 && (
                        <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs ${statusFilter === f ? 'bg-white/20' : 'bg-muted'}`}>
                          {stats[f]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Table */}
            {isLoading ? (
              <div className="p-6"><TableSkeleton rows={6} /></div>
            ) : filtered.length === 0 ? (
              <div className="p-6"><EmptyState type="messages" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sender</th>
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Channel</th>
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Category</th>
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sentiment</th>
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</th>
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Received</th>
                      <th className="p-3 w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((message) => {
                      const themes   = getMessageThemes(message);
                      const sentInfo = sentimentConfig[message.sentiment] || sentimentConfig.neutral;
                      const isNew    = message.status === 'new';

                      return (
                        <tr
                          key={message._id}
                          onClick={() => navigate(`/respond/${message._id}`)}
                          className={`cursor-pointer border-b border-border transition-colors hover:bg-muted/50 group ${isNew ? 'bg-[#f0856d]/5' : ''}`}
                        >
                          {/* Sender */}
                          <td className="p-3">
                            <p className={`text-sm ${isNew ? 'font-semibold' : ''} text-foreground`}>
                              {message.senderContact}
                            </p>
                            {message.senderName && (
                              <p className="text-xs text-muted-foreground">{message.senderName}</p>
                            )}
                          </td>

                          {/* Channel */}
                          <td className="p-3">
                            <ChannelIcon channel={message.channel} />
                          </td>

                          {/* Category */}
                          <td className="p-3">
                            {themes.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {themes.slice(0, 2).map(t => (
                                  <span
                                    key={t}
                                    className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground capitalize"
                                  >
                                    {t.replace(/-/g, ' ')}
                                  </span>
                                ))}
                                {themes.length > 2 && (
                                  <span className="text-xs text-muted-foreground">+{themes.length - 2}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>

                          {/* Sentiment */}
                          <td className="p-3">
                            {message.sentiment ? (
                              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${sentInfo.bg} ${sentInfo.color}`}>
                                {sentInfo.label}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>

                          {/* Status */}
                          <td className="p-3">
                            <StatusPill status={message.status} />
                          </td>

                          {/* Time */}
                          <td className="p-3">
                            <p className="text-sm text-muted-foreground">{formatAgo(message.receivedAt)}</p>
                            <p className="text-xs text-muted-foreground/60">{formatDate(message.receivedAt)}</p>
                          </td>

                          {/* Arrow hint */}
                          <td className="p-3">
                            <ChevronRight className="h-4 w-4 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Messages;